import { type ImageView, useImageTags } from '@vizit/api';
import { useShowTagging } from '@vizit/feature-flags';
import { FilterLinesIcon } from '@vizit/icons';
import { Asset, LibraryEmptyState } from '@vizit/molecules';
import { AssetGrid, ImageTags } from '@vizit/organisms';
import * as React from 'react';
import { useAssetGridViewer } from './asset-grid-viewer.hooks';

interface AssetGridViewerProps {
  /**
   * Additional CSS classes to apply to the grid container
   * @type {string}
   * @optional
   */
  className?: string;
  /**
   * Upload asset dialog to display in the empty state
   * @type {React.ReactNode}
   * @optional
   */
  uploadAssetDialog?: React.ReactNode;
  /**
   * Callback when an asset is clicked
   * @type {(id: string) => void}
   * @required
   */
  onNavigateToAsset: (id: string) => void;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Fixed images per row, e.g. from the thumbnail size slider. `undefined` keeps the responsive columns
   * @optional
   */
  columns?: number;
}

/**
 * AssetGridViewer component that fetches and displays portfolio assets
 * Uses the dev API for testing purposes
 *
 * @param props - The component props
 * @param props.className - Additional CSS classes
 */
const AssetGridViewer = React.memo(function AssetGridViewer(props: AssetGridViewerProps) {
  const { className, uploadAssetDialog, onNavigateToAsset, useOptimalInitialLoad, columns } = props;

  const {
    workspaceId,
    assets,
    totalCount,
    isLoading,
    isTagFiltered,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
  } = useAssetGridViewer({
    useOptimalInitialLoad,
    columns,
  });
  const showTagging = useShowTagging();
  const { tagsOf } = useImageTags();

  if (isTagFiltered && !isLoading && assets.length === 0) {
    return (
      <div className="flex w-full flex-grow items-center justify-center">
        <LibraryEmptyState
          heading="No assets match your filters."
          description="Try removing a filter, or tag an image from its tile."
          icon={FilterLinesIcon}
        />
      </div>
    );
  }

  return (
    <>
      <AssetGrid
        loadingCount={loadingCount}
        totalCount={totalCount}
        isLoading={isLoading}
        className={className}
        uploadAssetDialog={uploadAssetDialog}
        columns={columns}
      >
        {assets.map((asset: ImageView) => {
          const tagsWorkspaceId = showTagging ? (asset.workspace_id ?? workspaceId) : undefined;
          const tile = (
            <Asset
              onClick={() => {
                onNavigateToAsset(asset.id);
              }}
              onPress={() => {
                onNavigateToAsset(asset.id);
              }}
              key={asset.id}
              showScoreBar={false}
              isLoading={false}
            >
              <Asset.Image name={asset.name} src={asset.url_360 ?? asset.url} fallbackSrc={asset.url} />
            </Asset>
          );
          if (!tagsWorkspaceId) return tile;
          return (
            <div key={asset.id}>
              {tile}
              <ImageTags image={asset} tags={tagsOf(asset)} workspaceId={tagsWorkspaceId} />
            </div>
          );
        })}

        {/* Show loading assets when fetching next page */}
        {isFetchingNextPage && loadingCount
          ? Array.from({ length: loadingCount }, (_, index) => {
              const skeletonId = `asset-skeleton-next-${index}`;
              return (
                <Asset key={skeletonId} isLoading showScoreBar={false}>
                  <Asset.Image name="Loading..." src={null} />
                </Asset>
              );
            })
          : null}
      </AssetGrid>

      {/* Sentinel element for infinite scroll detection */}
      {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" aria-label="Loading more assets" />}
    </>
  );
});

export { AssetGridViewer };
export type { AssetGridViewerProps };
